import { apiClient } from './apiClient';
import { 
  LifetreeListRequest, 
  LifetreeListResponse, 
  LifetreeDetailResponse,
  CreateLifetreeRequest,
  UpdateLifetreeRequest,
  CreateLifetreeResponse,
  LifetreeMetadataResponse,
  ActivateLifetreeResponse,
  UpdateLifetreeImageUrlRequest,
  CreateNodeRequest,
  CreateNodeResponse,
  StructureUpdateRequest,
  NodeDetailResponse,
  UpdateNodeRequest,
  UpdateNodeResponse,
  ActivateNodeResponse,
  UpdateNodeImageUrlRequest,
  CreateCharacterStateRequest,
  CreateCharacterStateResponse,
  UpdateCharacterStateRequest,
  UpdateCharacterStateResponse,
  CreateRelationshipRequest,
  CreateRelationshipResponse,
  UpdateRelationshipRequest,
  UpdateRelationshipResponse,
  CreateCharacterRequest,
  CreateCharacterResponse,
  UpdateCharacterRequest,
  UpdateCharacterResponse,
  UpdateCharacterImageUrlRequest,
  UpdateCharacterImageUrlResponse,
  CharacterDetailResponse,
  ActivateStateResponse,
  StateImage,
  CreateImageResponse,
  ApproveImageRequest,
  ApproveImageResponse,
  FormalizeNodeCharactersResponse,
  CreateScenarioRequest,
  CreateScenarioResponse,
  CreateScenarioSampleRequest,
  CreateScenarioSampleResponse,
  CreateScenarioImageResponse,
  ApproveScenarioImageRequest,
  ApproveScenarioImageResponse,
  CreateCharacterStateCompletionRequest,
  CreateCharacterStateCompletionResponse,
  CreateCharacterNextStateCompletionRequest,
  CreateCharacterNextStateCompletionResponse,
  CreateStateImageRequest,
  CreateStateImagePromptRequest,
  CreateStateImagePromptResponse,
  CreateStateImageWithPredefinedPromptRequest,
  CreateStateImageWithPredefinedPromptResponse,
  UpdateScenarioRequest,
  UpdateScenarioResponse,
  ActivateScenarioResponse,
  CreateScenarioImagePromptRequest,
  CreateScenarioImagePromptResponse,
  CreateScenarioImageWithPredefinedPromptRequest,
  CreateScenarioImageWithPredefinedPromptResponse,
  NodeCharacterPlanResponse,
  NodeScenarioProposalsResponse,
  NodeSummaryResponse,
  LifetreeSuggestionResponse,
  NextNodeSuggestionsResponse
} from './models/Lifetree';

export const lifetreeApi = {
  /**
   * Tạo LifeTree mới
   */
  createLifetree: async (data: CreateLifetreeRequest): Promise<CreateLifetreeResponse> => {
    const response = await apiClient.post<CreateLifetreeResponse>('/api/v1/admin/lifetrees', data);
    return response.data;
  },

  /**
   * Cập nhật thông tin cơ bản LifeTree
   */
  updateLifetree: async (id: string, data: UpdateLifetreeRequest): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.put<LifetreeDetailResponse>(`/api/v1/admin/lifetrees/${id}`, data);
    return response.data;
  },

  /**
   * Sinh metadata (title, description, outline) cho Lifetree bằng AI
   */
  generateLifetreeMetadata: async (prompt: string, language: string = 'en'): Promise<LifetreeMetadataResponse> => {
    const response = await apiClient.post<LifetreeMetadataResponse>('/api/v1/admin/lifetrees/ai-generate-metadata', { prompt, language });
    return response.data;
  },

  /**
   * Tự động sinh danh sách Draft Nodes từ Outline
   */
  generateDraftNodes: async (lifeTreeId: string, language: string = 'en'): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.post<LifetreeDetailResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/ai-generate-nodes`, { language });
    return response.data;
  },

  /**
   * Lấy danh sách LifeTree với hỗ trợ phân trang và tìm kiếm
   */
  listLifeTrees: async (params: LifetreeListRequest = {}): Promise<LifetreeListResponse> => {
    const queryParams = new URLSearchParams();
    
    if (params.pageNumber) {
      queryParams.append('pageNumber', params.pageNumber.toString());
    }
    if (params.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params.search) {
      queryParams.append('search', params.search);
    }
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const response = await apiClient.get(`/api/v1/admin/lifetrees?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Lấy chi tiết LifeTree bao gồm nodes và characters
   */
  getLifetreeDetail: async (id: string): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.get(`/api/v1/admin/lifetrees/${id}`);
    return response.data;
  },

  /**
   * Kích hoạt LifeTree từ draft sang active
   */
  activateLifetree: async (id: string): Promise<ActivateLifetreeResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${id}/activations`);
    return response.data;
  },

  /**
   * Cập nhật image URL của LifeTree
   */
  updateLifetreeImageUrl: async (id: string, data: UpdateLifetreeImageUrlRequest): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${id}/image-urls`, data);
    return response.data;
  },

  /**
   * Tạo node mới trong LifeTree
   */
  createNode: async (lifeTreeId: string, nodeData: CreateNodeRequest): Promise<CreateNodeResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes`, nodeData);
    return response.data;
  },

  /**
   * Cập nhật cấu trúc LifeTree (level và connections của nodes)
   */
  updateStructure: async (id: string, data: StructureUpdateRequest): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${id}/structure`, data);
    return response.data;
  },

  // Node Detail APIs
  getNodeDetail: async (lifeTreeId: string, nodeId: string): Promise<NodeDetailResponse> => {
    const response = await apiClient.get(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}`);
    return response.data;
  },

  updateNode: async (lifeTreeId: string, nodeId: string, data: UpdateNodeRequest): Promise<UpdateNodeResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}`, data);
    return response.data;
  },

  /**
   * Kích hoạt Node từ draft sang active
   */
  activateNode: async (lifeTreeId: string, nodeId: string): Promise<ActivateNodeResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/activations`);
    return response.data;
  },

  /**
   * Cập nhật image URL của Node
   */
  updateNodeImageUrl: async (lifeTreeId: string, nodeId: string, data: UpdateNodeImageUrlRequest): Promise<NodeDetailResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/image-urls`, data);
    return response.data;
  },

  /**
   * AI đề xuất phác thảo nhân vật cho Node
   */
  proposeNodeCharacters: async (lifeTreeId: string, nodeId: string, language: string = 'en'): Promise<NodeCharacterPlanResponse> => {
    const response = await apiClient.post<NodeCharacterPlanResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/ai-propose-characters`, { language });
    return response.data;
  },

  /**
   * Reset nhân vật trong Node (xóa hết character states)
   */
  resetNodeCharacters: async (lifeTreeId: string, nodeId: string): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.post<LifetreeDetailResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/reset-characters`);
    return response.data;
  },

  /**
   * AI sinh mối quan hệ giữa các nhân vật trong Node
   */
  generateNodeRelationships: async (lifeTreeId: string, nodeId: string, refinedPlan: string, language: string = 'en'): Promise<LifetreeDetailResponse> => {
    const response = await apiClient.post<LifetreeDetailResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/ai-generate-relationships`, { refinedPlan, language });
    return response.data;
  },

  // Character APIs
  createCharacter: async (lifeTreeId: string, data: CreateCharacterRequest): Promise<CreateCharacterResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters`, data);
    return response.data;
  },

  // Character State APIs
  createCharacterState: async (lifeTreeId: string, characterId: string, data: CreateCharacterStateRequest): Promise<CreateCharacterStateResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states`, data);
    return response.data;
  },

  /**
   * Cập nhật Character State
   */
  updateCharacterState: async (lifeTreeId: string, characterId: string, stateId: string, data: UpdateCharacterStateRequest): Promise<UpdateCharacterStateResponse> => {
    const response = await apiClient.patch(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}`, data);
    return response.data;
  },

  // Relationship APIs
  createRelationship: async (characterId: string, stateId: string, data: CreateRelationshipRequest): Promise<CreateRelationshipResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/characters/${characterId}/states/${stateId}/relationships`, data);
    return response.data;
  },

  /**
   * Cập nhật Relationship
   */
  updateRelationship: async (lifeTreeId: string, characterId: string, stateId: string, relationshipId: string, data: UpdateRelationshipRequest): Promise<UpdateRelationshipResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}/relationships/${relationshipId}`, data);
    return response.data;
  },

  // Character Detail APIs
  getCharacterDetail: async (lifeTreeId: string, characterId: string): Promise<CharacterDetailResponse> => {
    const response = await apiClient.get(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}`);
    return response.data;
  },

  /**
   * Cập nhật thông tin chung của Character
   */
  updateCharacter: async (lifeTreeId: string, characterId: string, data: UpdateCharacterRequest): Promise<UpdateCharacterResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}`, data);
    return response.data;
  },

  /**
   * Cập nhật image URL của Character
   */
  updateCharacterImageUrl: async (lifeTreeId: string, characterId: string, data: UpdateCharacterImageUrlRequest): Promise<UpdateCharacterImageUrlResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/image-url`, data);
    return response.data;
  },

  // Character State Action APIs
  activateState: async (lifeTreeId: string, characterId: string, stateId: string): Promise<ActivateStateResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}/activations`);
    return response.data;
  },

  /**
   * Tạo prompt bằng AI cho Character State
   */
  createStateImagePrompt: async (lifeTreeId: string, characterId: string, stateId: string, data: CreateStateImagePromptRequest): Promise<CreateStateImagePromptResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}/image-prompts`, data);
    return response.data;
  },

  /**
   * Tạo ảnh cho Character State với prompt có sẵn
   */
  createStateImageWithPredefinedPrompt: async (lifeTreeId: string, characterId: string, stateId: string, data: CreateStateImageWithPredefinedPromptRequest): Promise<CreateStateImageWithPredefinedPromptResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}/predefined-images`, data);
    return response.data;
  },

  approveStateImage: async (lifeTreeId: string, characterId: string, stateId: string, data: ApproveImageRequest): Promise<ApproveImageResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/states/${stateId}/image-approvals`, data);
    return response.data;
  },

  // Scenario APIs
  /**
   * Tạo Scenario mới
   */
  createScenario: async (lifeTreeId: string, nodeId: string, data: CreateScenarioRequest): Promise<CreateScenarioResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios`, data);
    return response.data;
  },

  /**
   * Tạo Sample Scenario bằng AI
   */
  createScenarioSample: async (lifeTreeId: string, nodeId: string, data: CreateScenarioSampleRequest): Promise<CreateScenarioSampleResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenario-samples`, data);
    return response.data;
  },

  /**
   * Tạo prompt bằng AI cho Scenario
   */
  createScenarioImagePrompt: async (
    lifeTreeId: string,
    nodeId: string,
    scenarioId: string,
    data: CreateScenarioImagePromptRequest
  ): Promise<CreateScenarioImagePromptResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios/${scenarioId}/image-prompts`, data);
    return response.data;
  },

  /**
   * Tạo ảnh Scenario với prompt có sẵn
   */
  createScenarioImageWithPredefinedPrompt: async (
    lifeTreeId: string,
    nodeId: string,
    scenarioId: string,
    data: CreateScenarioImageWithPredefinedPromptRequest
  ): Promise<CreateScenarioImageWithPredefinedPromptResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios/${scenarioId}/predefined-images`, data);
    return response.data;
  },

  /**
   * Approve ảnh Scenario
   */
  approveScenarioImage: async (lifeTreeId: string, nodeId: string, scenarioId: string, data: ApproveScenarioImageRequest): Promise<ApproveScenarioImageResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios/${scenarioId}/image-approvals`, data);
    return response.data;
  },

  /**
   * Cập nhật Scenario
   */
  updateScenario: async (
    lifeTreeId: string,
    nodeId: string,
    scenarioId: string,
    data: UpdateScenarioRequest
  ): Promise<UpdateScenarioResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios/${scenarioId}`, data);
    return response.data;
  },

  /**
   * Kích hoạt Scenario từ draft sang active
   */
  activateScenario: async (
    lifeTreeId: string,
    nodeId: string,
    scenarioId: string
  ): Promise<ActivateScenarioResponse> => {
    const response = await apiClient.put(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/scenarios/${scenarioId}/activate`);
    return response.data;
  },

  /**
   * Đề xuất Scenario bằng AI
   */
  proposeNodeScenarios: async (
    lifeTreeId: string,
    nodeId: string,
    language: string = 'vi'
  ): Promise<NodeScenarioProposalsResponse> => {
    const response = await apiClient.post(
      `/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/ai-propose-scenarios`,
      { language }
    );
    return response.data;
  },

  /**
   * Tạo nguyên mẫu Character State bằng AI
   */
  createCharacterStateCompletion: async (lifeTreeId: string, characterId: string, data: CreateCharacterStateCompletionRequest): Promise<CreateCharacterStateCompletionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/state-completions`, data);
    return response.data;
  },

  /**
   * Tạo nguyên mẫu Character State tiếp theo bằng AI dựa trên state trước đó
   */
  createCharacterNextStateCompletion: async (lifeTreeId: string, characterId: string, data: CreateCharacterNextStateCompletionRequest): Promise<CreateCharacterNextStateCompletionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/lifetrees/${lifeTreeId}/characters/${characterId}/next-state-completions`, data);
    return response.data;
  },

  /**
   * Tự động tóm tắt nội dung Node bằng AI
   */
  generateNodeSummary: async (lifeTreeId: string, nodeId: string, language: string = 'vi'): Promise<NodeSummaryResponse> => {
    const response = await apiClient.post<NodeSummaryResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/ai-generate-summary`, { language });
    return response.data;
  },

  /**
   * AI gợi ý ý tưởng LifeTree mới dựa trên các truyện hiện có
   */
  suggestLifetreeIdeas: async (language: string = 'vi'): Promise<LifetreeSuggestionResponse> => {
    const response = await apiClient.post<LifetreeSuggestionResponse>('/api/v1/admin/lifetrees/ai-suggest-ideas', { language });
    return response.data;
  },

  /**
   * AI gợi ý Outline cho LifeTree hiện có dựa trên các Node
   */
  suggestOutline: async (lifeTreeId: string, language: string = 'vi'): Promise<{ outline: string }> => {
    const response = await apiClient.post<{ outline: string }>(`/api/v1/admin/lifetrees/${lifeTreeId}/ai-suggest-outline`, { language });
    return response.data;
  },

  /**
   * AI hiệu chỉnh Outline dựa trên bản thảo hiện tại và góp ý
   */
  refineOutline: async (
    lifeTreeId: string, 
    data: { currentOutline: string, feedback: string, language?: string }
  ): Promise<{ outline: string }> => {
    const response = await apiClient.post<{ outline: string }>(
      `/api/v1/admin/lifetrees/${lifeTreeId}/ai-refine-outline`, 
      { ...data, language: data.language || 'vi' }
    );
    return response.data;
  },

  /**
   * AI đề xuất các Node tiếp theo từ một Node cha cụ thể
   */
  suggestNextNodes: async (
    lifeTreeId: string, 
    nodeId: string, 
    language: string = 'vi'
  ): Promise<NextNodeSuggestionsResponse> => {
    const response = await apiClient.post<NextNodeSuggestionsResponse>(
      `/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/ai-suggest-next-nodes`,
      { language }
    );
    return response.data;
  },
  
  /**
   * Lấy danh sách ảnh của Node
   */
  getNodeImages: async (lifeTreeId: string, nodeId: string): Promise<StateImage[]> => {
    const response = await apiClient.get<StateImage[]>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/images`);
    return response.data;
  },

  /**
   * Tạo prompt bằng AI cho Node
   */
  createNodeImagePrompt: async (lifeTreeId: string, nodeId: string): Promise<CreateScenarioImagePromptResponse> => {
    const response = await apiClient.post<CreateScenarioImagePromptResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/image-prompts`, {});
    return response.data;
  },

  /**
   * Tạo ảnh Node với prompt có sẵn
   */
  createNodeImageWithPredefinedPrompt: async (
    lifeTreeId: string,
    nodeId: string,
    data: { description: string; involvedCharacterIds: string[] }
  ): Promise<CreateScenarioImageResponse> => {
    const response = await apiClient.post<CreateScenarioImageResponse>(`/api/v1/admin/lifetrees/${lifeTreeId}/nodes/${nodeId}/predefined-images`, data);
    return response.data;
  },

  /**
   * Lấy danh sách ảnh của Lifetree
   */
  getLifetreeImages: async (lifeTreeId: string): Promise<StateImage[]> => {
    const response = await apiClient.get<StateImage[]>(`/api/v1/admin/lifetrees/${lifeTreeId}/images`);
    return response.data;
  },

  /**
   * Tạo prompt bằng AI cho Lifetree dựa trên Node đại diện
   */
  createLifetreeImagePrompt: async (
    lifeTreeId: string, 
    nodeId: string, 
    description?: string
  ): Promise<CreateScenarioImagePromptResponse> => {
    const response = await apiClient.post<CreateScenarioImagePromptResponse>(
      `/api/v1/admin/lifetrees/${lifeTreeId}/image-prompts`, 
      { nodeId, description }
    );
    return response.data;
  },

  /**
   * Tạo ảnh Lifetree với prompt có sẵn
   */
  createLifetreeImageWithPredefinedPrompt: async (
    lifeTreeId: string,
    nodeId: string,
    data: { description: string; involvedCharacterIds: string[] }
  ): Promise<CreateScenarioImageResponse> => {
    const response = await apiClient.post<CreateScenarioImageResponse>(
      `/api/v1/admin/lifetrees/${lifeTreeId}/predefined-images`, 
      { nodeId, ...data }
    );
    return response.data;
  },
};
